import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Search,
  TrendingUp,
  TrendingDown,
  Refresh,
  AccountBalance,
  MoreVert,
  Add,
} from '@mui/icons-material';
import { Holding } from '../../types';

interface PortfolioData {
  holdings: Holding[]; // Executed holdings only
  pendingHoldings?: Holding[]; // Pending holdings separately
  summary: {
    totalValue: number;
    totalPnL: number;
    totalPremiumCollected?: number;
    holdingCount: number;
    optionCount?: number;
    pendingCount?: number;
  };
}

interface RealizedSummary {
  today: number;
  week: number;
  allTime: number;
  cycleCount: number;
  winCount: number;
}

const Portfolio: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [realized, setRealized] = useState<RealizedSummary | null>(null);
  const [filterType, setFilterType] = useState('ALL');
  const [portfolioData, setPortfolioData] = useState<PortfolioData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [contextMenu, setContextMenu] = useState<{mouseX: number; mouseY: number; holdingId: string} | null>(null);
  const [botModalOpen, setBotModalOpen] = useState(false);
  const [selectedHolding, setSelectedHolding] = useState<Holding | null>(null);
  const [bots, setBots] = useState<any[]>([]);

  const fetchPortfolio = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/trade/portfolio`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Transform the API data to match our Holding interface (with hierarchical options)
        const holdings: Holding[] = data.portfolio.holdings.map((holding: any) => ({
          id: holding.id,
          symbol: holding.symbol,
          secType: holding.secType,
          exchange: holding.exchange,
          quantity: holding.quantity,
          averagePrice: parseFloat(holding.averagePrice || 0),
          currentPrice: parseFloat(holding.currentPrice || 0),
          marketValue: parseFloat(holding.marketValue || 0),
          unrealizedPnL: parseFloat(holding.unrealizedPnL || 0),
          createdAt: holding.createdAt,
          status: holding.status || 'EXECUTED',
          orderId: holding.orderId,
          options: holding.options ? holding.options.map((opt: any) => ({
            id: opt.id,
            symbol: opt.symbol,
            secType: opt.secType,
            exchange: opt.exchange,
            quantity: opt.quantity,
            averagePrice: parseFloat(opt.averagePrice || 0),
            currentPrice: parseFloat(opt.currentPrice || 0),
            marketValue: parseFloat(opt.marketValue || 0),
            unrealizedPnL: parseFloat(opt.unrealizedPnL || 0),
            createdAt: opt.createdAt,
            status: opt.status || 'EXECUTED',
            orderId: opt.orderId,
          })) : undefined,
        }));

        // Transform pending holdings separately
        const pendingHoldings: Holding[] = data.portfolio.pendingHoldings 
          ? data.portfolio.pendingHoldings.map((holding: any) => ({
              id: holding.id,
              symbol: holding.symbol,
              secType: holding.secType,
              exchange: holding.exchange,
              quantity: holding.quantity,
              averagePrice: parseFloat(holding.averagePrice || 0),
              currentPrice: parseFloat(holding.currentPrice || 0),
              marketValue: parseFloat(holding.marketValue || 0),
              unrealizedPnL: parseFloat(holding.unrealizedPnL || 0),
              createdAt: holding.createdAt,
              status: holding.status || 'PENDING',
              orderId: holding.orderId,
              options: holding.options ? holding.options.map((opt: any) => ({
                id: opt.id,
                symbol: opt.symbol,
                secType: opt.secType,
                exchange: opt.exchange,
                quantity: opt.quantity,
                averagePrice: parseFloat(opt.averagePrice || 0),
                currentPrice: parseFloat(opt.currentPrice || 0),
                marketValue: parseFloat(opt.marketValue || 0),
                unrealizedPnL: parseFloat(opt.unrealizedPnL || 0),
                createdAt: opt.createdAt,
                status: opt.status || 'PENDING',
                orderId: opt.orderId,
              })) : undefined,
            }))
          : [];

        setPortfolioData({
          holdings, // Executed holdings only
          pendingHoldings, // Pending holdings separately
          summary: {
            totalValue: parseFloat(data.portfolio.summary.totalValue) || 0,
            totalPnL: parseFloat(data.portfolio.summary.totalPnL) || 0,
            totalPremiumCollected: parseFloat(data.portfolio.summary.totalPremiumCollected) || 0,
            holdingCount: parseInt(data.portfolio.summary.holdingCount) || 0,
            optionCount: parseInt(data.portfolio.summary.optionCount) || 0,
            pendingCount: parseInt(data.portfolio.summary.pendingCount) || 0
          }
        });
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch portfolio');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Portfolio fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRealized = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/trade/realized-summary`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRealized(data.summary);
      }
    } catch (err) {
      console.error('Realized summary fetch error:', err);
    }
  };

  useEffect(() => {
    fetchPortfolio();
    fetchRealized();
    fetchBots();
  }, []);

  const fetchBots = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/bot/list`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBots(data.bots || []);
      }
    } catch (err) {
      console.error('Failed to fetch bots:', err);
    }
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleAddToBot = () => {
    handleCloseContextMenu();
    setBotModalOpen(true);
  };

  const handleCloseBotModal = () => {
    setBotModalOpen(false);
    setSelectedHolding(null);
  };

  const handleConfirmAddToBot = async (strategy: string) => {
    if (!selectedHolding) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/bot/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          symbol: selectedHolding.symbol,
          strategy
        }),
      });

      const result = await response.json();

      if (response.ok) {
        alert(`Automated trading enabled for ${selectedHolding.symbol}. The bot will automatically sell covered calls.`);
        handleCloseBotModal();
        fetchBots(); // Refresh bots list
        // Refresh portfolio after a moment to show any new positions
        setTimeout(() => {
          fetchPortfolio();
        }, 3000);
      } else {
        alert(result.message || 'Failed to enable automated trading');
      }
    } catch (error) {
      console.error('Error adding bot:', error);
      alert('Network error. Please try again.');
    }
  };


  // Executed holdings come from portfolioData.holdings
  // Pending holdings come from portfolioData.pendingHoldings (separate array)
  const executedHoldings = portfolioData?.holdings || [];
  const pendingHoldings = portfolioData?.pendingHoldings || [];

  const filteredExecutedHoldings = executedHoldings.filter(holding => {
    const matchesSearch = holding.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (holding.options && holding.options.some(opt => opt.symbol.toLowerCase().includes(searchTerm.toLowerCase())));
    const matchesType = filterType === 'ALL' || 
      holding.secType === filterType || 
      (holding.options && holding.options.some(opt => opt.secType === filterType));
    return matchesSearch && matchesType;
  });

  const filteredPendingHoldings = pendingHoldings.filter(holding => {
    const matchesSearch = holding.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (holding.options && holding.options.some(opt => opt.symbol.toLowerCase().includes(searchTerm.toLowerCase())));
    const matchesType = filterType === 'ALL' || 
      holding.secType === filterType || 
      (holding.options && holding.options.some(opt => opt.secType === filterType));
    return matchesSearch && matchesType;
  });

  const getGainLossColor = (unrealizedPnL: number) => {
    return unrealizedPnL >= 0 ? 'success' : 'error';
  };

  const getGainLossIcon = (unrealizedPnL: number) => {
    return unrealizedPnL >= 0 ? <TrendingUp /> : <TrendingDown />;
  };

  const calculateGainLossPercentage = (holding: Holding) => {
    if (holding.averagePrice && holding.averagePrice > 0) {
      return ((holding.currentPrice - holding.averagePrice) / holding.averagePrice) * 100;
    }
    return 0;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <IconButton onClick={() => { fetchPortfolio(); fetchRealized(); }} color="primary">
            <Refresh />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Portfolio
        </Typography>
        <IconButton onClick={fetchPortfolio} color="primary">
          <Refresh />
        </IconButton>
      </Box>

      {/* Portfolio Summary */}
      {portfolioData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccountBalance sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Total Value
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  ${Number(portfolioData.summary.totalValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {getGainLossIcon(portfolioData.summary.totalPnL)}
                  <Typography variant="h6" sx={{ fontWeight: 600, ml: 1 }}>
                    Unrealized P&L
                  </Typography>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: getGainLossColor(portfolioData.summary.totalPnL) + '.main'
                  }}
                >
                  ${Number(portfolioData.summary.totalPnL).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {getGainLossIcon(realized?.allTime ?? 0)}
                  <Typography variant="h6" sx={{ fontWeight: 600, ml: 1 }}>
                    Realized P&L
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: getGainLossColor(realized?.allTime ?? 0) + '.main'
                  }}
                >
                  ${Number(realized?.allTime ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                {realized && (
                  <Typography variant="caption" color="text.secondary">
                    Today ${realized.today.toFixed(0)} · 7d ${realized.week.toFixed(0)} · {realized.winCount}/{realized.cycleCount} wins
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUp sx={{ color: 'success.main', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Premiums Collected
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  ${Number(portfolioData.summary.totalPremiumCollected || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Holdings
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {portfolioData.summary.holdingCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search by symbol..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 200 }}
            />
            
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                label="Type"
              >
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="STOCK">Stocks</MenuItem>
                <MenuItem value="CALL">Calls</MenuItem>
                <MenuItem value="PUT">Puts</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Executed Holdings Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Executed Holdings ({filteredExecutedHoldings.length})
            </Typography>
          </Box>

          {filteredExecutedHoldings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No executed holdings found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {executedHoldings.length === 0 
                  ? "You don't have any executed holdings yet. Start trading to build your portfolio!"
                  : "Try adjusting your search or filter criteria."
                }
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Symbol</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Current Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Market Value</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Gain/Loss</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Gain/Loss %</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Purchase Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredExecutedHoldings.map((holding) => (
                    <React.Fragment key={holding.id}>
                      {/* Stock row */}
                      <TableRow hover>
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {holding.symbol}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={holding.secType === 'STK' ? 'Stock' : 'Option'} 
                            size="small"
                            color={holding.secType === 'STK' ? 'primary' : 'secondary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {holding.quantity.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            ${Number(holding.currentPrice).toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            ${Number(holding.marketValue).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getGainLossIcon(holding.unrealizedPnL)}
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500,
                                ml: 0.5,
                                color: getGainLossColor(holding.unrealizedPnL) + '.main'
                              }}
                            >
                              ${holding.unrealizedPnL.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: getGainLossColor(holding.unrealizedPnL) + '.main'
                            }}
                          >
                            {calculateGainLossPercentage(holding).toFixed(2)}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(holding.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                      <TableCell>
                        {holding.secType === 'STK' && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {bots.some(b => b.symbol === holding.symbol && b.status === 'ACTIVE') && (
                              <Chip 
                                label="Bot Active" 
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            )}
                            {holding.quantity >= 100 && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setContextMenu({
                                    mouseX: e.clientX - 2,
                                    mouseY: e.clientY - 4,
                                    holdingId: holding.id
                                  });
                                  setSelectedHolding(holding);
                                }}
                              >
                                <MoreVert />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      </TableRow>
                      {/* Options rows (child items) - only show executed options */}
                      {holding.options && holding.options
                        .filter(opt => opt.status !== 'PENDING')
                        .map((option) => (
                        <TableRow 
                          key={option.id} 
                          sx={{ bgcolor: 'action.hover' }}
                        >
                          <TableCell>
                            <Box sx={{ pl: 3, display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>└─</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {option.symbol}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label="Option" 
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {option.quantity.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              ${Number(option.currentPrice).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              ${Number(option.marketValue).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {getGainLossIcon(option.unrealizedPnL)}
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 500,
                                  ml: 0.5,
                                  color: getGainLossColor(option.unrealizedPnL) + '.main'
                                }}
                              >
                                ${option.unrealizedPnL.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 500,
                                color: getGainLossColor(option.unrealizedPnL) + '.main'
                              }}
                            >
                              {calculateGainLossPercentage(option).toFixed(2)}%
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(option.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

      {/* Pending Orders Section */}
      {filteredPendingHoldings.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
                Pending Orders ({filteredPendingHoldings.length})
              </Typography>
              <Chip
                label="Awaiting Fill"
                size="small"
                color="info"
                variant="outlined"
              />
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Symbol</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Order Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estimated Value</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Order Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPendingHoldings.map((holding) => (
                    <React.Fragment key={holding.id}>
                      {/* Stock row */}
                      {holding.status === 'PENDING' && (
                        <TableRow hover sx={{ bgcolor: 'rgba(201,133,0,.14)' }}>
                          <TableCell>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {holding.symbol}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={holding.secType === 'STK' ? 'Stock' : 'Option'}
                              size="small"
                              color={holding.secType === 'STK' ? 'primary' : 'secondary'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {holding.quantity.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              ${Number(holding.currentPrice).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              ${Number(holding.marketValue).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="Pending"
                              size="small"
                              color="warning"
                              variant="filled"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(holding.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={async () => {
                                try {
                                  const r = await fetch(
                                    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/trade/${holding.id}/cancel`,
                                    {
                                      method: 'POST',
                                      headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                                      },
                                    }
                                  );
                                  if (r.ok) fetchPortfolio();
                                } catch (err) {
                                  console.error('Cancel failed:', err);
                                }
                              }}
                            >
                              Cancel
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Options rows (child items) - only show pending options */}
                      {holding.options && holding.options
                        .filter(opt => opt.status === 'PENDING')
                        .map((option) => (
                        <TableRow 
                          key={option.id} 
                          sx={{ bgcolor: 'rgba(201,133,0,.14)' }}
                        >
                          <TableCell>
                            <Box sx={{ pl: 3, display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>└─</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {option.symbol}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label="Option" 
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {option.quantity.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              ${Number(option.currentPrice).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              ${Number(option.marketValue).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label="Pending" 
                              size="small"
                              color="warning"
                              variant="filled"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(option.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {selectedHolding && bots.some(b => b.symbol === selectedHolding.symbol && b.status === 'ACTIVE') ? (
          <MenuItem disabled>
            <ListItemIcon>
              <Add fontSize="small" />
            </ListItemIcon>
            <ListItemText>Bot Active (Automated)</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem onClick={handleAddToBot}>
            <ListItemIcon>
              <Add fontSize="small" />
            </ListItemIcon>
            <ListItemText>Enable Automated Trading</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Add to Bot Modal */}
      <Dialog open={botModalOpen} onClose={handleCloseBotModal} maxWidth="sm" fullWidth>
        <DialogTitle>Add Security to Bot</DialogTitle>
        <DialogContent>
          {selectedHolding && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Enable automated trading for <strong>{selectedHolding.symbol}</strong>?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You have {selectedHolding.quantity} shares. The bot will automatically execute the selected strategy:
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Strategy</InputLabel>
                <Select
                  defaultValue="COVERED_CALL"
                  label="Strategy"
                >
                  <MenuItem value="COVERED_CALL">Covered Call</MenuItem>
                </Select>
              </FormControl>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Covered Call Strategy:</strong> The bot will automatically sell covered calls when conditions are met:
                  <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                    <li>Strike price: 7% above current price</li>
                    <li>Expiration: Nearest contract with at least 3 days remaining</li>
                    <li>Requires: At least 100 shares (you have {selectedHolding.quantity})</li>
                    <li>Execution: Only when there's no existing covered call position</li>
                    <li>Monitoring: Bot checks periodically - executes once per position cycle</li>
                  </ul>
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBotModal}>Cancel</Button>
          <Button 
            onClick={() => handleConfirmAddToBot('COVERED_CALL')} 
            variant="contained"
          >
            Enable Automated Trading
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Portfolio;